<template>
  <div class="boxscore-demo">
    <!-- Dateline / broadsheet header -->
    <div class="boxscore-demo__dateline">
      <div class="boxscore-demo__dateline-inner">
        <span class="dateline__item">Vol. {{ buildVolume }} &middot; N° {{ buildIssue }}</span>
        <span class="dateline__item dateline__item--center">
          {{ formattedDate }}
          <span class="dateline__dot">♦</span>
          Pressbox Edition &mdash; Game Program
        </span>
        <span class="dateline__item dateline__item--right">Tonight's Simulated Boxscore</span>
      </div>
    </div>

    <!-- Hero Section -->
    <BoxscoreHeroSection :game="data?.game" />

    <!-- Boxscore Section -->
    <BoxscoreSection
      v-if="data"
      :batting="data.batting"
      :pitching="data.pitching"
      :home-abbr="data.game.home_abbr"
      :away-abbr="data.game.away_abbr"
    />

    <!-- Win Probability Chart -->
    <WinProbabilityChart
      v-if="data"
      :simulation="data.simulation"
      :home-label="data.game.home_abbr"
      :away-label="data.game.away_abbr"
    />

    <!-- Player Projections -->
    <PlayerProjectionGrid v-if="data" :players="keyPlayers" />

    <!-- Conditional Scenarios -->
    <ConditionalScenariosPanel v-if="data" :scenarios="data.scenarios" />

    <!-- CTA Section -->
    <BoxscoreCTASection />

    <!-- Colophon / Footer -->
    <footer class="colophon">
      <div class="colophon__rule" aria-hidden="true"></div>
      <div class="colophon__inner">
        <div class="colophon__left">
          <span class="colophon__brand">HomerunData</span>
          <span class="colophon__tag">Pressbox Edition</span>
        </div>
        <div class="colophon__center">
          <p>
            Set in <em>Fraunces</em>, <em>Newsreader</em>, <em>Syne</em>, and
            <em>JetBrains Mono</em>.<br />
            Typeset from first pitch to final out.
          </p>
        </div>
        <div class="colophon__right">
          &copy; {{ currentYear }} &middot; Build {{ buildVolume }}.{{ buildIssue }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import BoxscoreHeroSection from '../components/boxscore/BoxscoreHeroSection.vue'
import BoxscoreSection from '../components/boxscore/BoxscoreSection.vue'
import WinProbabilityChart from '../components/boxscore/WinProbabilityChart.vue'
import PlayerProjectionGrid from '../components/boxscore/PlayerProjectionGrid.vue'
import ConditionalScenariosPanel from '../components/boxscore/ConditionalScenariosPanel.vue'
import BoxscoreCTASection from '../components/boxscore/BoxscoreCTASection.vue'

interface BoxscoreData {
  game: {
    home_team: string
    home_abbr: string
    away_team: string
    away_abbr: string
    date: string
  }
  simulation: {
    sim_count: number
    home_win_pct: number
    away_win_pct: number
    home_run_dist: number[]
    away_run_dist: number[]
    total_run_dist: number[]
  }
  batting: Array<{
    player_id: string
    name: string
    team: string
    position: string
    ab: number
    h: number
    r: number
    hr: number
    rbi: number
    mean_hits: number
    mean_hr: number
    mean_rbi: number
    hit_distribution: number[]
    hr_distribution: number[]
  }>
  pitching: Array<{
    player_id: string
    name: string
    team: string
    ip: number
    h: number
    r: number
    er: number
    bb: number
    k: number
  }>
  scenarios: Array<{
    condition: string
    baseline_win_pct: number
    scenario_win_pct: number
  }>
}

const data = ref<BoxscoreData | null>(null)
const currentYear = computed((): number => new Date().getFullYear())
const now = new Date()

const formattedDate = computed((): string =>
  now
    .toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    .toUpperCase()
)

const commitSha = (import.meta.env.VITE_COMMIT_SHA as string | undefined) ?? ''
const buildNumber = (import.meta.env.VITE_BUILD_NUMBER as string | undefined) ?? ''

const buildVolume = computed((): string => (commitSha ? commitSha.slice(0, 7) : 'dev'))
const buildIssue = computed((): string => {
  if (buildNumber) return buildNumber
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000
  )
  return String(dayOfYear)
})

const keyPlayers = computed(() => {
  if (!data.value) return []
  // Return first 6 batting players for projection grid
  return data.value.batting.slice(0, 6)
})

onMounted(async () => {
  try {
    const response = await fetch('/fixtures/boxscore-demo.json')
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    data.value = await response.json()
  } catch (error) {
    console.error('Failed to load fixture data:', error)
  }
})
</script>

<style scoped>
.boxscore-demo {
  padding-top: 4.75rem; /* below masthead */
  color: var(--ink);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ─── DATELINE ─────────────────────────────────────────────────────── */
.boxscore-demo__dateline {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.boxscore-demo__dateline-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.55rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.5rem;
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
}

.dateline__item--center {
  text-align: center;
}

.dateline__item--right {
  text-align: right;
}

.dateline__dot {
  display: inline-block;
  margin: 0 0.6em;
  color: var(--ox);
  transform: translateY(-1px);
}

/* ─── COLOPHON / FOOTER ───────────────────────────────────────────── */
.colophon {
  margin-top: auto;
  background: var(--paper-deep);
}

.colophon__rule {
  height: 5px;
  border-top: 3px solid var(--ink);
  border-bottom: 1px solid var(--ink);
}

.colophon__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;
  font-family: var(--font-ui);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ink-medium);
}

.colophon__left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.colophon__brand {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.3rem;
  text-transform: none;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.colophon__tag {
  color: var(--ox);
  font-size: 0.6rem;
  letter-spacing: 0.26em;
}

.colophon__center {
  text-align: center;
  font-family: var(--font-editorial);
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-faint);
}

.colophon__center em {
  color: var(--ink);
  font-style: italic;
}

.colophon__right {
  text-align: right;
  color: var(--ink-faint);
}

@media (max-width: 700px) {
  .boxscore-demo__dateline-inner {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    text-align: center;
  }

  .dateline__item--right {
    text-align: center;
  }

  .colophon__inner {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .colophon__left,
  .colophon__right {
    text-align: center;
  }
}
</style>
